import React, { useEffect, useRef } from "react"
import { HeaderMenuFieldsFragment } from "../../../../../graphql-types"
import {
  focusInCycle,
  getVisibleFocusableElements,
  isVisibleFocusable,
  setInert,
} from "../../../../utils/accessibility"
import LanguageSelector, { RelativeLanguagePaths } from "../header-tool-bar/language-selector/language-selector"
import Accordion from "./accordion/accordion"
import * as styles from "./side-menu.module.scss"

type Props = {
  isOpen: boolean
  closeMenu: () => void
  headerMenu: HeaderMenuFieldsFragment
  locale: string
  defaultLocale: string
  relativeLanguagePaths: RelativeLanguagePaths
}

const HAMBURGER_SELECTOR = ".hamburger-react"

const SideMenu: React.FC<Props> = ({ headerMenu, isOpen, closeMenu, locale, defaultLocale, relativeLanguagePaths }) => {
  const dialogRef = useRef<HTMLDivElement>(null)
  const previouslyFocusedRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) {
      return
    }

    setInert(dialog, !isOpen)
    if (!isOpen) {
      return
    }

    previouslyFocusedRef.current = document.activeElement as HTMLElement

    const getTrapElements = () => {
      const inside = getVisibleFocusableElements(dialog)
      const hamburger = document.querySelector<HTMLElement>(HAMBURGER_SELECTOR)
      return hamburger && isVisibleFocusable(hamburger) ? [hamburger, ...inside] : inside
    }

    const focusTimer = window.setTimeout(() => {
      getVisibleFocusableElements(dialog)[0]?.focus()
    }, 0)

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" || event.key === "Esc") {
        event.preventDefault()
        event.stopPropagation()
        closeMenu()
        return
      }

      if (event.key !== "Tab") {
        return
      }

      // Own the Tab cycle: logo sits between burger and panel in DOM order.
      event.preventDefault()
      event.stopPropagation()
      focusInCycle(getTrapElements(), document.activeElement, event.shiftKey)
    }

    document.addEventListener("keydown", onKeyDown, true)
    return () => {
      window.clearTimeout(focusTimer)
      document.removeEventListener("keydown", onKeyDown, true)
      previouslyFocusedRef.current?.focus?.()
    }
  }, [isOpen, closeMenu])

  function buildAccordionButton(mainMenuLabel: string, mainMenuPage, subMenu, externalPageLink?: string) {
    const displayedLinks = subMenu
      .filter(link => link.headerDisplay)
      .filter(link => (link.page && link.page.slug) || link.externalPage)
      .map(link => ({
        label: link.label,
        href: link.externalPage
          ? link.externalPage
          : locale === defaultLocale
            ? `/${link.page?.slug}`
            : `/${locale}/${link.page?.slug}`,
      }))
    const hasSubMenu = displayedLinks.length > 0

    if (!mainMenuLabel && !hasSubMenu) {
      return null
    }

    return (
      <Accordion title={mainMenuLabel}>
        {(mainMenuPage || externalPageLink) && (
          <li>
            <a
              href={
                mainMenuPage
                  ? locale === defaultLocale
                    ? `/${mainMenuPage.slug}`
                    : `/${locale}/${mainMenuPage.slug}`
                  : externalPageLink
              }
            >
              {mainMenuLabel}
            </a>
          </li>
        )}
        {displayedLinks.map((link, index) => (
          <li key={index}>
            <a href={link.href}>{link.label}</a>
          </li>
        ))}
      </Accordion>
    )
  }

  const menuItems = isOpen
    ? (headerMenu.menuSection || [])
        .filter(menuItem => menuItem?.headerDisplay)
        .map((menuItem, index) => (
          <React.Fragment key={menuItem.label || index}>
            {buildAccordionButton(menuItem.label, menuItem.page, menuItem.subMenu, menuItem.externalPage)}
          </React.Fragment>
        ))
    : null

  return (
    <div className={styles.sideNav}>
      <div
        ref={dialogRef}
        id="side-menu-dialog"
        role="dialog"
        aria-modal={isOpen}
        aria-label={headerMenu.mobileMenuAriaLabel}
        tabIndex={-1}
        className={isOpen ? `${styles.sideMenu} ${styles.active}` : styles.sideMenu}
      >
        <nav aria-label={headerMenu.mobileMenuAriaLabel}>
          <ul id="sideMenuUlTag">{menuItems}</ul>
          {isOpen && (
            <div className={styles.languageSelectorContainer}>
              <LanguageSelector locale={locale} relativeLanguagePaths={relativeLanguagePaths} />
            </div>
          )}
        </nav>
      </div>

      {isOpen && (
        <button type="button" className={styles.blur} aria-label={headerMenu.closeMenuAriaLabel} onClick={closeMenu} />
      )}
    </div>
  )
}

export default SideMenu
